import React, { useEffect, useState } from "react";
import axios from "axios";
import { FaFileExcel, FaTrash, FaEdit, FaSearch, FaTimes, FaChevronDown } from "react-icons/fa";
import { saveAs } from "file-saver";
import * as XLSX from "xlsx";
import Swal from "sweetalert2";

const DaigTestList = () => {
  const [tests, setTests] = useState([]);
  const [search, setSearch] = useState("");
  const [currentPage, setCurrentPage] = useState(1);
  const testsPerPage = 5;

  const [editingTest, setEditingTest] = useState(null);
  const [editForm, setEditForm] = useState({
    name: "",
    category: "",
    price: "",
    gender: "", // NEW: Added gender field
    description: "",
    subTests: "",
    instruction: "",
    fastingRequired: false,
    homeCollectionAvailable: false,
    reportIn24Hrs: false,
    diagnostics: []
  });

  const [diagnosticsList, setDiagnosticsList] = useState([]);
  const [loadingDiagnostics, setLoadingDiagnostics] = useState(false);
  const [dropdownOpen, setDropdownOpen] = useState(false);

  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [successMessage, setSuccessMessage] = useState("");

  useEffect(() => {
    fetchTests();
    fetchDiagnostics();
  }, []);

  const fetchTests = async () => {
    try {
      setLoading(true);
      const res = await axios.get("https://api.elthiumhealth.com/api/admin/alltests");
      if (res.data?.tests) {
        setTests(res.data.tests || []);
      }
    } catch (error) {
      console.error("Failed to fetch tests:", error);
      setError("Failed to fetch tests. Please try again later.");
    } finally {
      setLoading(false);
    }
  };

  const fetchDiagnostics = async () => {
    try {
      setLoadingDiagnostics(true);
      const response = await axios.get("https://api.elthiumhealth.com/api/admin/alldiagnostics");
      
      if (response.data && response.data.data) {
        setDiagnosticsList(response.data.data);
      } else {
        setDiagnosticsList([]);
      }
    } catch (error) {
      console.error("Error fetching diagnostics:", error);
      Swal.fire({
        icon: "error",
        title: "Error",
        text: "Failed to load diagnostics list",
      });
      setDiagnosticsList([]);
    } finally {
      setLoadingDiagnostics(false);
    }
  };

  const handleDelete = async (id) => {
    if (!window.confirm("Are you sure you want to delete this test?")) return;

    try {
      const res = await axios.delete(`https://api.elthiumhealth.com/api/admin/deletetest/${id}`);
      if (res.status === 200) {
        setTests(tests.filter((test) => test._id !== id));
        setSuccessMessage("Test deleted successfully.");
        setTimeout(() => setSuccessMessage(""), 3000);
      }
    } catch (error) {
      console.error("Failed to delete test:", error);
      setError("Failed to delete test.");
      setTimeout(() => setError(""), 3000);
    }
  };

  const handleEditClick = (test) => {
    setEditingTest(test);
    setEditForm({
      name: test.name || "",
      category: test.category || "",
      price: test.price || "",
      gender: test.gender || "", // NEW: Add gender from test
      description: test.description || "",
      subTests: Array.isArray(test.subTests) ? test.subTests.join(", ") : "",
      instruction: test.instruction || "",
      fastingRequired: test.fastingRequired || false,
      homeCollectionAvailable: test.homeCollectionAvailable || false,
      reportIn24Hrs: test.reportIn24Hrs || false,
      diagnostics: Array.isArray(test.diagnostics) 
        ? test.diagnostics.filter(d => d && d._id).map(d => d._id)
        : []
    });
    setDropdownOpen(false);
  };

  const handleEditChange = (e) => {
    const { name, value, type, checked } = e.target;
    setEditForm({ 
      ...editForm, 
      [name]: type === 'checkbox' ? checked : value 
    });
  };

  const handleDiagnosticChange = (diagnosticId) => {
    const currentDiagnostics = [...editForm.diagnostics];
    
    if (currentDiagnostics.includes(diagnosticId)) {
      setEditForm({
        ...editForm,
        diagnostics: currentDiagnostics.filter(id => id !== diagnosticId)
      });
    } else {
      setEditForm({
        ...editForm,
        diagnostics: [...currentDiagnostics, diagnosticId]
      });
    }
  };

  const handleSelectAll = () => {
    if (editForm.diagnostics.length === diagnosticsList.length) {
      setEditForm({ ...editForm, diagnostics: [] });
    } else {
      const allDiagnosticIds = diagnosticsList.map(d => d._id);
      setEditForm({ ...editForm, diagnostics: allDiagnosticIds });
    }
  };

  const handleEditSubmit = async () => {
    try {
      // Validate required fields
      if (!editForm.name || !editForm.price || !editForm.gender) {
        setError("Test name, price and gender are required.");
        setTimeout(() => setError(""), 3000);
        return;
      }

      const payload = {
        name: editForm.name,
        category: editForm.category,
        price: editForm.price,
        gender: editForm.gender, // NEW: Include gender in payload
        description: editForm.description,
        subTests: editForm.subTests.split(",").map((s) => s.trim()).filter((s) => s),
        instruction: editForm.instruction,
        fastingRequired: editForm.fastingRequired,
        homeCollectionAvailable: editForm.homeCollectionAvailable,
        reportIn24Hrs: editForm.reportIn24Hrs,
        diagnosticIds: editForm.diagnostics
      };

      console.log("Sending test update data:", payload);

      const res = await axios.put(
        `https://api.elthiumhealth.com/api/admin/updatetest/${editingTest._id}`,
        payload
      );

      if (res.status === 200) {
        setSuccessMessage("Test updated successfully.");
        setEditingTest(null);
        fetchTests();
        setTimeout(() => setSuccessMessage(""), 3000);
      }
    } catch (error) {
      console.error("Failed to update test:", error);
      console.error("Error response:", error.response?.data);
      setError("Failed to update test.");
      setTimeout(() => setError(""), 3000);
    }
  };

  const formatAssignedDiagnostics = (diagnostics) => {
    if (!Array.isArray(diagnostics) || diagnostics.length === 0) {
      return "-";
    }
    
    const diagnosticNames = diagnostics
      .filter(diagnostic => diagnostic && diagnostic.name)
      .map(diagnostic => diagnostic.name);
    
    if (diagnosticNames.length === 0) return "-";
    
    return diagnosticNames.join(", ");
  };

  const getDiagnosticNameById = (id) => {
    const diagnostic = diagnosticsList.find(d => d._id === id);
    return diagnostic ? diagnostic.name : "Unknown Diagnostic";
  };

  // Format gender with colored badge
  const formatGender = (gender) => {
    if (!gender) return "-";
    
    let badgeClass = "";
    switch(gender.toLowerCase()) {
      case 'male':
        badgeClass = "bg-blue-100 text-blue-800";
        break;
      case 'female':
        badgeClass = "bg-pink-100 text-pink-800";
        break;
      case 'both':
        badgeClass = "bg-gray-100 text-gray-800";
        break;
      default:
        badgeClass = "bg-gray-100 text-gray-800";
    }
    
    return (
      <span className={`px-2 py-1 rounded-full text-xs font-medium ${badgeClass}`}>
        {gender}
      </span>
    );
  };

  const filteredTests = tests.filter((test) => {
    if (!test) return false;
    
    const searchLower = search.toLowerCase();
    return (
      (test.name && test.name.toLowerCase().includes(searchLower)) ||
      (test.category && test.category.toLowerCase().includes(searchLower)) ||
      (test.gender && test.gender.toLowerCase().includes(searchLower)) || // NEW: Search by gender
      (test.description && test.description.toLowerCase().includes(searchLower)) ||
      (test.instruction && test.instruction.toLowerCase().includes(searchLower)) ||
      (test.price && test.price.toString().includes(search)) ||
      (test.diagnostics && test.diagnostics.some(d => 
        d && d.name && d.name.toLowerCase().includes(searchLower)
      )) ||
      search === ""
    );
  });

  const indexOfLastTest = currentPage * testsPerPage;
  const indexOfFirstTest = indexOfLastTest - testsPerPage;
  const currentTests = filteredTests.slice(indexOfFirstTest, indexOfLastTest);
  const totalPages = Math.ceil(filteredTests.length / testsPerPage);

  const exportToExcel = () => {
    const formatted = tests.map(({ _id, __v, createdAt, updatedAt, ...rest }) => {
      const formattedItem = {};
      for (const key in rest) {
        if (key === 'diagnostics') {
          formattedItem[key] = formatAssignedDiagnostics(rest[key]);
        } else if (key === 'subTests') {
          formattedItem[key] = Array.isArray(rest[key]) ? rest[key].join(", ") : rest[key];
        } else {
          formattedItem[key] = rest[key] === null || rest[key] === undefined ? "N/A" : rest[key];
        }
      }
      return formattedItem;
    });
    
    const worksheet = XLSX.utils.json_to_sheet(formatted);
    const workbook = XLSX.utils.book_new();
    XLSX.utils.book_append_sheet(workbook, worksheet, "Tests");
    const excelBuffer = XLSX.write(workbook, { bookType: "xlsx", type: "array" });
    const blob = new Blob([excelBuffer], {
      type: "application/vnd.openxmlformats-officedocument.spreadsheetml.sheet;charset=UTF-8",
    });
    saveAs(blob, "tests.xlsx");
  };

  const formatSubTests = (subTests) => {
    if (!Array.isArray(subTests) || subTests.length === 0) return "-";
    return subTests.join(", ");
  };

  const formatText = (text) => {
    if (!text) return "-";
    return text.length > 50 ? `${text.substring(0, 50)}...` : text;
  };

  const formatBoolean = (value) => {
    return value ? "Yes" : "No";
  };

  if (loading) {
    return (
      <div className="p-4 bg-white rounded shadow">
        <div className="flex justify-center items-center h-64">
          <div className="text-xl">Loading tests...</div>
        </div>
      </div>
    );
  }

  return (
    <div className="p-4 bg-white rounded shadow">
      <div className="flex justify-between items-center mb-4">
        <h2 className="text-xl font-semibold">All Diagnostic Tests</h2>
        <span className="text-sm text-gray-500">
          {filteredTests.length} record(s) found
        </span>
      </div>

      {/* Status Messages */}
      {error && (
        <div className="mb-4 p-3 bg-red-100 text-red-700 rounded">
          {error}
        </div>
      )}
      {successMessage && (
        <div className="mb-4 p-3 bg-green-100 text-green-700 rounded">
          {successMessage}
        </div>
      )}

      <div className="mb-4 flex gap-2 flex-wrap">
        <div className="relative flex-1 min-w-[200px]">
          <FaSearch className="absolute left-3 top-3 text-gray-400" />
          <input
            type="text"
            className="pl-10 pr-4 py-2 border rounded text-sm w-full"
            placeholder="Search by name, gender, category, description, instruction, price or diagnostic..."
            value={search}
            onChange={(e) => setSearch(e.target.value)}
          />
          {search && (
            <button
              onClick={() => setSearch("")}
              className="absolute right-3 top-3 text-gray-400 hover:text-gray-600"
            >
              <FaTimes />
            </button>
          )}
        </div>
        <button
          onClick={exportToExcel}
          className="px-4 py-2 bg-green-500 text-white rounded text-sm flex items-center gap-1"
        >
          <FaFileExcel /> Export to Excel
        </button>
      </div>

      <div className="overflow-x-auto">
        <table className="w-full border rounded text-sm">
          <thead className="bg-gray-200">
            <tr>
              <th className="p-2 border">Name</th>
              <th className="p-2 border">Gender</th> {/* NEW: Gender column */}
              <th className="p-2 border">Category</th>
              <th className="p-2 border">MRP</th>
              <th className="p-2 border">Description</th>
              <th className="p-2 border">Sub Tests</th>
              <th className="p-2 border">Instructions</th>
              <th className="p-2 border">Fasting</th>
              <th className="p-2 border">Home Collection</th>
              <th className="p-2 border">24hr Report</th>
              <th className="p-2 border">Assigned Diagnostics</th>
              <th className="p-2 border">Actions</th>
            </tr>
          </thead>
          <tbody>
            {currentTests.length > 0 ? (
              currentTests.map((test) => (
                <tr key={test._id} className="hover:bg-gray-50 border-b">
                  <td className="p-2 border">{test.name || "-"}</td>
                  <td className="p-2 border">{formatGender(test.gender)}</td>
                  <td className="p-2 border">{test.category || "-"}</td>
                  <td className="p-2 border">₹{test.price || "0"}</td>
                  <td className="p-2 border" title={test.description}>
                    {formatText(test.description)}
                  </td>
                  <td className="p-2 border" title={formatSubTests(test.subTests)}>
                    {formatText(formatSubTests(test.subTests))}
                  </td>
                  <td className="p-2 border" title={test.instruction}>
                    {formatText(test.instruction)}
                  </td>
                  <td className="p-2 border">
                    {formatBoolean(test.fastingRequired)}
                  </td>
                  <td className="p-2 border">
                    {formatBoolean(test.homeCollectionAvailable)}
                  </td>
                  <td className="p-2 border">
                    {formatBoolean(test.reportIn24Hrs)}
                  </td>
                  <td className="p-2 border" title={formatAssignedDiagnostics(test.diagnostics)}>
                    {formatText(formatAssignedDiagnostics(test.diagnostics))}
                  </td>
                  <td className="p-2 border flex gap-2 justify-center">
                    <button
                      onClick={() => handleEditClick(test)}
                      className="text-blue-500 hover:text-blue-700 p-1"
                      title="Edit"
                    >
                      <FaEdit />
                    </button>
                    <button
                      onClick={() => handleDelete(test._id)}
                      className="text-red-500 hover:text-red-700 p-1"
                      title="Delete"
                    >
                      <FaTrash />
                    </button>
                  </td>
                </tr>
              ))
            ) : (
              <tr>
                <td colSpan="12" className="text-center p-4"> {/* Updated colSpan to 12 */}
                  {search ? "No matching tests found" : "No tests available"}
                </td>
              </tr>
            )}
          </tbody>
        </table>
      </div>

      {/* Pagination */}
      {filteredTests.length > 0 && (
        <div className="flex justify-between items-center mt-4">
          <button
            onClick={() => setCurrentPage(currentPage - 1)}
            className="px-4 py-2 bg-gray-300 text-gray-700 rounded disabled:opacity-50"
            disabled={currentPage === 1}
          >
            Previous
          </button>
          <div className="flex items-center justify-center">
            <span className="text-sm">
              Page {currentPage} of {totalPages} ({filteredTests.length} items)
            </span>
          </div>
          <button
            onClick={() => setCurrentPage(currentPage + 1)}
            className="px-4 py-2 bg-gray-300 text-gray-700 rounded disabled:opacity-50"
            disabled={currentPage === totalPages || totalPages === 0}
          >
            Next
          </button>
        </div>
      )}

      {/* Edit Modal */}
      {editingTest && (
        <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50 p-4">
          <div className="bg-white p-6 rounded-lg shadow-xl w-full max-w-4xl max-h-[95vh] overflow-auto">
            <div className="flex justify-between items-center mb-6 pb-4 border-b">
              <h3 className="text-xl font-semibold text-gray-800">Edit Diagnostic Test</h3>
              <button
                onClick={() => setEditingTest(null)}
                className="text-gray-500 hover:text-gray-700 text-lg"
              >
                <FaTimes />
              </button>
            </div>
            
            <div className="space-y-6">
              {/* Basic Information */}
              <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-2">
                    Test Name <span className="text-red-500">*</span>
                  </label>
                  <input
                    type="text"
                    name="name"
                    value={editForm.name}
                    onChange={handleEditChange}
                    className="w-full border border-gray-300 rounded-lg px-3 py-2 focus:outline-none focus:ring-2 focus:ring-blue-500 focus:border-transparent"
                    placeholder="Enter test name"
                  />
                </div>
                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-2">
                    Category
                  </label>
                  <input
                    type="text"
                    name="category"
                    value={editForm.category}
                    onChange={handleEditChange}
                    className="w-full border border-gray-300 rounded-lg px-3 py-2 focus:outline-none focus:ring-2 focus:ring-blue-500 focus:border-transparent"
                    placeholder="Enter category"
                  />
                </div>
                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-2">
                    Price (₹) <span className="text-red-500">*</span>
                  </label>
                  <input
                    type="number"
                    name="price"
                    value={editForm.price}
                    onChange={handleEditChange}
                    className="w-full border border-gray-300 rounded-lg px-3 py-2 focus:outline-none focus:ring-2 focus:ring-blue-500 focus:border-transparent"
                    placeholder="Enter price"
                    min="0"
                    step="0.01"
                  />
                </div>
                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-2">
                    Gender <span className="text-red-500">*</span>
                  </label>
                  <select
                    name="gender"
                    value={editForm.gender}
                    onChange={handleEditChange}
                    className="w-full border border-gray-300 rounded-lg px-3 py-2 focus:outline-none focus:ring-2 focus:ring-blue-500 focus:border-transparent"
                    required
                  >
                    <option value="">Select Gender</option>
                    <option value="Male">Male</option>
                    <option value="Female">Female</option>
                    <option value="Both">Both</option>
                  </select>
                </div>
              </div>

              {/* Checkboxes */}
              <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
                <label className="flex items-center space-x-3">
                  <input
                    type="checkbox"
                    name="fastingRequired"
                    checked={editForm.fastingRequired}
                    onChange={handleEditChange}
                    className="h-5 w-5 text-blue-600 border-gray-300 rounded focus:ring-blue-500"
                  />
                  <span className="text-sm font-medium text-gray-700">Fasting Required</span>
                </label>
                <label className="flex items-center space-x-3">
                  <input
                    type="checkbox"
                    name="homeCollectionAvailable"
                    checked={editForm.homeCollectionAvailable}
                    onChange={handleEditChange}
                    className="h-5 w-5 text-blue-600 border-gray-300 rounded focus:ring-blue-500"
                  />
                  <span className="text-sm font-medium text-gray-700">Home Collection Available</span>
                </label>
                <label className="flex items-center space-x-3">
                  <input
                    type="checkbox"
                    name="reportIn24Hrs"
                    checked={editForm.reportIn24Hrs}
                    onChange={handleEditChange}
                    className="h-5 w-5 text-blue-600 border-gray-300 rounded focus:ring-blue-500"
                  />
                  <span className="text-sm font-medium text-gray-700">Report in 24 Hours</span>
                </label>
              </div>

              {/* Diagnostics Selection */}
              <div className="border border-gray-200 rounded-lg p-4">
                <div className="flex justify-between items-center mb-3">
                  <h4 className="font-semibold text-gray-800">Assign Diagnostics Centers</h4>
                  <span className="text-xs text-gray-500">
                    {editForm.diagnostics.length} selected
                  </span>
                </div>
                
                {loadingDiagnostics ? (
                  <div className="text-center py-4">
                    <div className="text-sm text-gray-500">Loading diagnostics...</div>
                  </div>
                ) : diagnosticsList.length > 0 ? (
                  <div>
                    {/* Dropdown */}
                    <div className="relative">
                      <button
                        type="button"
                        onClick={() => setDropdownOpen(!dropdownOpen)}
                        className="w-full p-3 border border-gray-300 rounded-lg bg-white text-left flex justify-between items-center focus:outline-none focus:ring-2 focus:ring-blue-500 focus:border-transparent"
                      >
                        <span className="text-gray-700">
                          {editForm.diagnostics.length === 0 
                            ? "Choose Diagnostics" 
                            : `${editForm.diagnostics.length} diagnostic(s) selected`
                          }
                        </span>
                        <FaChevronDown className={`text-gray-400 transition-transform ${dropdownOpen ? 'transform rotate-180' : ''}`} />
                      </button>

                      {dropdownOpen && (
                        <div className="absolute z-10 w-full mt-1 bg-white border border-gray-300 rounded-lg shadow-lg max-h-60 overflow-y-auto">
                          {/* Select All Option */}
                          <div className="p-3 border-b bg-gray-50">
                            <label className="flex items-center cursor-pointer">
                              <input
                                type="checkbox"
                                checked={editForm.diagnostics.length === diagnosticsList.length}
                                onChange={handleSelectAll}
                                className="h-4 w-4 text-blue-600 border-gray-300 rounded focus:ring-blue-500"
                              />
                              <span className="ml-2 text-sm font-medium text-gray-700">
                                {editForm.diagnostics.length === diagnosticsList.length 
                                  ? "Deselect All" 
                                  : "Select All"
                                }
                              </span>
                            </label>
                          </div>

                          {/* Diagnostics List */}
                          <div className="p-2">
                            {diagnosticsList.map((diagnostic) => (
                              <label
                                key={diagnostic._id}
                                className="flex items-center p-3 hover:bg-gray-100 rounded-lg cursor-pointer transition-colors"
                              >
                                <input
                                  type="checkbox"
                                  checked={editForm.diagnostics.includes(diagnostic._id)}
                                  onChange={() => handleDiagnosticChange(diagnostic._id)}
                                  className="h-4 w-4 text-blue-600 border-gray-300 rounded focus:ring-blue-500"
                                />
                                <span className="ml-3 text-sm text-gray-700">
                                  {diagnostic.name || "Unnamed Diagnostic"}
                                </span>
                              </label>
                            ))}
                          </div>
                        </div>
                      )}
                    </div>
                    
                    {/* Selected Diagnostics Preview */}
                    {editForm.diagnostics.length > 0 && (
                      <div className="mt-3 p-3 bg-blue-50 rounded-lg border border-blue-200">
                        <h5 className="font-medium text-blue-800 text-sm mb-2">Selected Diagnostics:</h5>
                        <div className="flex flex-wrap gap-2">
                          {editForm.diagnostics.map(diagnosticId => (
                            <span 
                              key={diagnosticId} 
                              className="px-3 py-1 bg-blue-100 text-blue-800 rounded-full text-xs font-medium flex items-center gap-1"
                            >
                              {getDiagnosticNameById(diagnosticId)}
                              <button
                                type="button"
                                onClick={() => handleDiagnosticChange(diagnosticId)}
                                className="text-blue-600 hover:text-blue-800 ml-1"
                              >
                                <FaTimes className="text-xs" />
                              </button>
                            </span>
                          ))}
                        </div>
                      </div>
                    )}
                  </div>
                ) : (
                  <div className="text-center py-4 text-sm text-gray-500">
                    No diagnostics centers available
                  </div>
                )}
              </div>

              {/* Description */}
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-2">
                  Description
                </label>
                <textarea
                  name="description"
                  value={editForm.description}
                  onChange={handleEditChange}
                  className="w-full border border-gray-300 rounded-lg px-3 py-2 focus:outline-none focus:ring-2 focus:ring-blue-500 focus:border-transparent"
                  rows="3"
                  placeholder="Enter test description"
                />
              </div>

              {/* Sub Tests */}
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-2">
                  Sub Tests (comma separated)
                </label>
                <textarea
                  name="subTests"
                  value={editForm.subTests}
                  onChange={handleEditChange}
                  className="w-full border border-gray-300 rounded-lg px-3 py-2 focus:outline-none focus:ring-2 focus:ring-blue-500 focus:border-transparent"
                  rows="2"
                  placeholder="Enter sub tests separated by commas"
                />
              </div>

              {/* Instructions */}
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-2">
                  Instructions
                </label>
                <textarea
                  name="instruction"
                  value={editForm.instruction}
                  onChange={handleEditChange}
                  className="w-full border border-gray-300 rounded-lg px-3 py-2 focus:outline-none focus:ring-2 focus:ring-blue-500 focus:border-transparent"
                  rows="3"
                  placeholder="Enter instructions for the test"
                />
              </div>
            </div>

            {/* Action Buttons */}
            <div className="flex justify-end gap-3 mt-8 pt-6 border-t">
              <button 
                onClick={() => setEditingTest(null)} 
                className="px-6 py-2 border border-gray-300 text-gray-700 rounded-lg hover:bg-gray-50 transition-colors"
              >
                Cancel
              </button>
              <button 
                onClick={handleEditSubmit} 
                className="px-6 py-2 bg-blue-600 text-white rounded-lg hover:bg-blue-700 transition-colors flex items-center gap-2"
              >
                <FaEdit /> Update Test
              </button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
};

export default DaigTestList;